/** 한국어. See `en.ts` for what each key is and what it has to fit.
 *
 * The rail glyphs stay on the Latin WMO symbols: Korean charts have no
 * conventional single-syllable name for these fields, and a two-syllable one
 * would not read at 44px. The pressure centers do have a convention — 고 / 저
 * — and take it. */

import type { MessageKey } from "./en";

export const ko: Record<MessageKey, string> = {
  metaDescription:
    "무료 실시간 날씨 지도를 브라우저에서: GFS·ECMWF·AIFS 수치예보, 레이더와 위성 영상 애니메이션, 태풍 경로, 3D 레이더, 스큐-T 단열선도.",
  pageTitleLive: "{variable} 예보 지도 · {model} {hours}H",
  pageTitleLiveObservation: "{variable} 관측 지도 · {model} · 최근 {hours}시간",
  mapAria: "전 지구 기온·강수 예보 지도",
  modelSwitchAria: "데이터셋",
  variableSwitchAria: "기상 요소",
  varTemp: "기온",
  varPrecip: "강수",
  varWind: "바람",
  varSolar: "일사",
  varRadar: "레이더",
  varOrog: "지형",
  varInfrared: "적외 10.4",
  varShortwave: "단파 3.9",
  varDustRgb: "RGB",
  varDustCf: "DEBRA",
  varZhouye: "ZHOUYE",
  varTrueColor: "컬러",
  varGust: "돌풍",
  varCloud: "운량",
  varCape: "대류",
  varPwat: "기둥",
  varPtype: "유형",
  varCin: "억제",
  varHpbl: "고도",
  varVis: "시정",
  varDewPoint: "이슬점",
  varFeelsLike: "체감",
  varOmega: "연직",
  varThetaE: "상당온위",
  varCat: "청천난류",
  varInflow: "습윤류",
  varFront: "전선대",
  varHotspot: "고온점",
  varFfwi: "산불 기상",
  varFwalert: "건조 강풍",
  varSst: "해수면",
  varIce: "해빙",
  varWave: "파랑",
  varAod: "전체",
  varPm: "2.5",
  varDustsfc: "황사",
  varTsoil: "지온",
  varSoilw: "토양수분",
  varHumidity: "상대",
  varVapourFlux: "수증기",
  varPressure: "해면",
  varHeight: "고도",
  varPressureField: "기압장",
  varUnknownLayer: "레이어",

  glyphTmp2m: "T",
  glyphPrate: "R",
  glyphWind10m: "W",
  glyphDswrf: "S",
  glyphCref: "Z",
  glyphIr104: "위",
  glyphIr039: "단",
  glyphDustrgb: "먼",
  glyphDustcf: "신",
  glyphZhouye: "주",
  glyphTruecolor: "색",
  glyphGust: "G",
  glyphTcdc: "N",
  glyphCape: "C",
  glyphVis: "V",
  glyphPwat: "수",
  glyphCin: "억",
  glyphHpbl: "층",
  glyphPtype: "형",
  glyphDpt2m: "D",
  glyphAptmp2m: "A",
  glyphVvel: "ω",
  glyphThetae: "θ",
  glyphCat: "ε",
  glyphInflow: "습",
  glyphFront: "전",
  glyphHotspot: "열",
  glyphFfwi: "화",
  glyphFwalert: "경",
  glyphTmpsfc: "해",
  glyphIce: "빙",
  glyphWave: "파",
  glyphAod: "τ",
  glyphPm: "µ",
  glyphDustsfc: "황",
  glyphTsoil: "지",
  glyphSoilw: "토",
  glyphPressure: "P",
  glyphRh: "U",
  glyphQflux: "F",
  tipTemp: "기온",
  tipHumidity: "상대습도",
  tipWind: "바람",
  tipPressure: "기압 및 지위고도",
  tipVapourFlux: "수증기 플럭스",
  tipCloud: "운량",
  tipOmega: "연직 속도",
  tipThetaE: "상당온위",
  tipCat: "청천난류",
  tipIce: "해빙",
  tipWave: "파랑",
  tipAod: "에어로졸 광학 두께",
  tipPm: "미세먼지",
  tipTsoil: "토양 온도",
  tipSoilw: "토양 수분",
  tipSatellite: "위성 영상",
  levelSwitchAria: "등압면",
  levelCaption: "등압면",
  linesCaption: "등압선",
  accumCaption: "누적",
  accumRateGloss: "강수 강도",
  accumTotalGloss: "{window} 누적",
  modelHeading: "데이터셋",
  linesRemoveTitle: "다시 누르면 등치선을 지웁니다",
  fieldSectionAria: "필드",
  overlaySectionAria: "오버레이",
  markSectionAria: "마크",
  tipMore: "이 실행의 모든 레이어",
  varMore: "레이어",
  glyphMore: "+",
  fieldSheetAria: "레이어",
  fieldSheetHeading: "레이어",
  fieldGroupTemperature: "기온",
  fieldGroupMoisture: "수분",
  fieldGroupSoil: "토양",
  fieldGroupWind: "바람",
  fieldGroupDynamics: "역학",
  fieldGroupRadiation: "복사와 시정",
  fieldGroupAerosol: "에어로졸과 대기질",
  fieldGroupOcean: "해양",
  fieldGroupHydrology: "하천·홍수",
  fieldGroupSatellite: "위성",
  fieldGroupOther: "기타",
  fieldGroupImagery: "일별 영상: NASA GIBS",
  gibsTrueColor: "트루",
  gibsFalseColor: "화재",
  gibsTipTrue: "트루컬러",
  gibsTipFire: "화재 위색: 탄 자리는 짙은 빨강",
  varParticles: "입자",
  glyphParticles: "입",
  varVolume: "3D 구름",
  glyphVolume: "구",
  levelAlone: "선만",
  levelAloneTitle: "등압선만 표시: 필드를 끄고, 다시 누르면 복원",
  creditsTrigger: "자료 출처",
  creditsHeading: "자료 출처 및 감사의 글",
  creditsGfs: "예보 자료: GFS 0.25° 및 sflux",
  creditsHrrr: "예보 자료: HRRR 3 km, 미국 본토",
  creditsGefsAero: "에어로졸 예보: GEFS-Aerosols 0.25°, 광학 두께와 지상 미세먼지",
  creditsCmaDust: "황사 예보: CMA-CW v1.0/DUST(WMO SDS-WAS 아시아 센터) 0.15°, 아시아, WMO WIS2 경유",
  creditsSilam: "대기질 예보: FMI SILAM v6.1 전 지구 0.2°, CC BY 4.0",
  creditsCfs: "계절 예보: CFSv2, 9개월, 6시간 간격",
  creditsEcmwf: "수정된 ECMWF 자료 포함, CC BY 4.0",
  creditsIfsHres: "ECMWF IFS를 각색, 제공 ECMWF, CC BY 4.0 라이선스; Open-Meteo 경유; IFS HRES 약 9 km, 0.1°로 재샘플링",
  creditsMrms: "레이더 관측: MRMS, 미국 본토",
  creditsNexrad: "단일 레이더: 반사도와 시선 속도, 미국",
  creditsJma: "레이더 강수: 기상청 실황예보, 일본 (재격자화)",
  creditsHimawari: "위성 영상: 히마와리 9호 AHI, 일본 기상청, NOAA 오픈 데이터 경유 (재투영)",
  creditsGoes: "위성 영상: GOES-19 동부·GOES-18 서부 ABI, NOAA NESDIS, NOAA 오픈 데이터 경유 (재투영)",
  creditsMeteosat: "위성 영상: EUMETSAT Meteosat 데이터를 가공하여 사용 {year}; Meteosat-12 FCI, 매 정시 주기, CC-BY-4.0 (재투영)",
  creditsSounding: "라디오존데 관측: GTS TEMP 전문 (WIS2 경유), 각국 기상기관",
  creditsAirport: "공항 METAR·TAF: 각국 기상기관 (NOAA/NWS AWC 경유)",
  creditsFirms: "활성 화재: LANCE FIRMS NRT, VIIRS 375 m 및 MODIS, NASA ESDIS",
  creditsGibs: "일별 영상: MODIS·VIIRS 보정 반사도, NASA ESDIS",
  creditsRadar: "레이더 합성도",
  creditsBasemap: "배경지도: © OpenStreetMap 기여자",
  creditsTerrain: "지형 음영: © Mapterhorn, 고도 데이터 출처 목록",
  creditsPeaks: "산 이름과 고도: CC0",
  creditsCode: "소스 코드: ringsaturn/xue",
  caseTag: "사례",
  caseSummaryAria: "사례 요약",
  runCycle: "모델 초기시각",
  recordSpan: "기록 기간",
  latestObservation: "최신 관측",
  latestNowcast: "최신 초단기 예측",
  awaitingData: "자료 대기 중",
  validTimeLabel: "유효시각",
  observationTimeLabel: "관측시각",
  statsAria: "상세 통계",
  statsCloseAria: "상세 통계 닫기",
  bufferedFrames: "버퍼된 프레임",
  downloadedBytes: "내려받은 용량",
  formatLabel: "형식",
  formatZarr: "Zarr",
  preloadProgressAria: "예보 자료 미리 읽기 진행률",
  statDataset: "데이터셋",
  statGrid: "격자",
  statFrameCache: "프레임 캐시",
  statDecodeTime: "디코딩 시간",
  statDecodeRate: "디코딩 속도",
  statViewport: "화면 표본화",
  statConnection: "네트워크",
  awaitingManifest: "매니페스트 대기 중",
  legendPrateAria: "강수 강도 색상 눈금",
  timelineAria: "예보 시각 조작",
  playAnimation: "애니메이션 재생",
  pauseAnimation: "애니메이션 일시정지",
  playbackSpeed: "재생 속도",
  exportOpenAria: "애니메이션 내보내기",
  exportSheetAria: "애니메이션 내보내기",
  exportHeading: "내보내기",
  exportFormat: "형식",
  exportSize: "크기",
  exportFps: "프레임 속도",
  exportRange: "범위",
  exportFrom: "시작",
  exportTo: "끝",
  exportRun: "내보내기",
  exportCancel: "취소",
  exportSummary: "{size}, {frames}프레임, {seconds}초, 약 {bytes}",
  exportCapturing: "캡처 중 {done}/{total}",
  exportEncoding: "인코딩 중 {percent}%",
  exportFailed: "내보내기 실패",
  readingManifest: "매니페스트 읽는 중",
  forecastHourAria: "예보 시간",
  forecastDaysAria: "일별 예보 구간",
  observationDaysAria: "일별 구간",
  mapMenuAria: "지도 메뉴",
  menuStats: "상세 통계",
  menuCopyDebug: "디버그 정보 복사",
  errorTitle: "자료를 표시할 수 없습니다",
  retry: "다시 읽기",
  langPickerAria: "언어",
  langPickerHeading: "언어",
  themeToggleAria: "밝게 / 어둡게 전환",
  viewGlobeAria: "지구본 보기",
  viewTerrainAria: "3D 지형",
  viewContoursAria: "등고선",
  viewResetNorthAria: "북쪽으로 재설정",
  viewResetPitchAria: "기울기 재설정",
  viewBoxAria: "폭풍 확대: 영역을 드래그",
  viewSectionAria: "연직 단면: 선을 드래그",
  viewDistanceAria: "거리 측정: 지점을 클릭하고 마지막 점을 다시 클릭하면 끝",
  viewAreaAria: "면적 측정: 꼭짓점을 클릭하고 첫 점이나 마지막 점을 다시 클릭하면 끝",
  particlesToggle: "입자 애니메이션",
  particlesToggleAria: "바람 입자 애니메이션 전환",

  dataInterrupted: "자료 읽기가 중단되었습니다",
  errorHint: "{message}. make mvp를 실행하고 make serve로 페이지를 열었는지 확인하세요.",
  loadFailed: "읽기 실패",
  webglLost: "WebGL을 사용할 수 없습니다. 브라우저를 완전히 종료한 뒤 다시 여세요",
  saveData: "절약",
  debugCopied: "디버그 정보를 복사했습니다",
  copyFailed: "복사 실패",
  bundleResident: "번들을 모두 버퍼했습니다",
  viewportResident: "현재 화면을 모두 버퍼했습니다",
  streamingOnDemand: "필요한 만큼 스트리밍 중",
  receivingBundle: "예보 번들 받는 중",
  receivingBundlePercent: "번들 받는 중 {percent}%",
  readingIndex: "자료 색인 읽는 중",
  initializingDecoder: "디코더 초기화 중",
  framesReady: "{count} 프레임 준비 완료",
  readingData: "자료 읽는 중",
  bundleLoadFailed: "예보 번들을 읽지 못했습니다",
  workerStartFailed: "디코딩 Worker를 시작하지 못했습니다",
  decodeFailed: "디코딩 실패",
  frameDecodeFailed: "프레임 디코딩 실패: {message}",
  legendAria: "{label} 색상 눈금",
  legendInflow: "온난 습윤 기류",
  legendFront: "전선대 (θe 경도)",
  legendHotspot: "산불 고온점 (3.9 µm 문맥 판정)",
  legendFfwi: "Fosberg 산불 기상 지수",
  legendFwalert: "상대습도 30 % 미만, 돌풍 10 m/s 이상",
  legendDustDust: "먼지",
  legendDustThickHigh: "두꺼운 상층운",
  legendDustThinHigh: "얇은 상층운",
  legendDustMid: "중층운",
  legendDustLow: "따뜻한 하층운",
  legendDustDesert: "뜨거운 사막 지표",
  legendDustSurface: "해면과 식생 지표",
  legendTruecolorCloud: "구름과 눈",
  legendTruecolorSea: "해면",
  legendTruecolorLand: "육지와 식생",
  legendTruecolorNightCloud: "밤의 구름 (적외)",
  legendTruecolorNightGround: "밤의 맑은 하늘",
  legendPtypeRain: "비",
  legendPtypeFreezingRain: "어는 비",
  legendPtypeSnow: "눈",
  legendPtypeIcePellets: "싸락눈",
  legendDisrpBelow: "1.5년 빈도 미만",
  legendDisrp1p5: "≥ 1.5년 빈도",
  legendDisrp2: "≥ 2년 빈도",
  legendDisrp5: "≥ 5년 빈도",
  legendDisrp20: "≥ 20년 빈도",
  legendCatLight: "약함",
  legendCatModerate: "보통",
  legendCatModerateSevere: "보통–심함",
  legendCatSevere: "심함",
  varLabelTmp2m: "지상 2 m 기온",
  varLabelPrate: "강수 강도",
  varLabelDswrf: "일사량",
  varLabelWind10m: "지상 10 m 바람",
  varLabelWind80m: "80 m 바람",
  varLabelWind100m: "100 m 바람",
  varLabelCref: "레이더 합성 반사도",
  varLabelRefl3d: "3차원 레이더 반사도",
  varLabelCloud3d: "3차원 구름물",
  varLabelOrog: "지형 고도",
  varLabelIr104: "적외 영상 (10.4 µm 휘도 온도)",
  varLabelIr039: "단파 적외 (3.9 µm 휘도 온도)",
  varLabelDustrgb: "먼지 RGB (적외 합성)",
  varLabelDustcf: "DEBRA 황사 신뢰도",
  varLabelZhouye: "ZHOUYE 황사 신뢰도",
  varLabelTruecolor: "트루컬러 (낮에는 가시광, 밤에는 적외 구름)",
  varLabelGust: "돌풍",
  varLabelTcdc: "전운량",
  varLabelCape: "대류가용잠재에너지",
  varLabelCin: "대류억제",
  varLabelVis: "시정",
  varLabelDpt2m: "2 m 이슬점",
  varLabelAptmp2m: "2 m 체감온도",
  varLabelLcdc: "하층운량",
  varLabelMcdc: "중층운량",
  varLabelHcdc: "상층운량",
  varLabelPwat: "가강수량",
  varLabelHpbl: "행성 경계층 높이",
  varLabelPtype: "강수 유형",
  varLabelTmpsfc: "해수면 온도(육지는 지표면 온도)",
  varLabelIcec: "해빙 밀집도",
  varLabelIcetk: "해빙 두께",
  varLabelWave: "파랑: 유의파고와 파향",
  varLabelHtsgw: "유의파고",
  varLabelPerpw: "주파주기",
  varLabelDirpw: "주파향",
  varLabelWindwave: "풍랑(유의파고와 파향)",
  varLabelSwell: "너울(유의파고와 파향, 제1성분)",
  varLabelWvhgt: "풍랑 유의파고",
  varLabelWvper: "풍랑 평균주기",
  varLabelWvdir: "풍랑 파향",
  varLabelSwhgt: "너울 유의파고(제1성분)",
  varLabelSwper: "너울 평균주기(제1성분)",
  varLabelSwdir: "너울 파향(제1성분)",
  varLabelAod: "550 nm 에어로졸 광학 두께",
  varLabelAoddust: "황사 광학 두께",
  varLabelAodsalt: "해염 광학 두께",
  varLabelAodsulf: "황산염 광학 두께",
  varLabelAodorg: "유기탄소 광학 두께",
  varLabelAodbc: "블랙카본 광학 두께",
  varLabelPm25: "지상 PM2.5 농도",
  varLabelPm10: "지상 PM10 농도",
  varLabelPm10dust: "지상 황사 PM10 농도",
  varLabelDustsfc: "지상 황사 농도",
  varLabelTsoil1: "토양 온도, 1층",
  varLabelTsoil2: "토양 온도, 2층",
  varLabelTsoil3: "토양 온도, 3층",
  varLabelTsoil4: "토양 온도, 4층",
  varLabelSoilw1: "토양 수분(체적), 1층",
  varLabelSoilw2: "토양 수분(체적), 2층",
  varLabelSoilw3: "토양 수분(체적), 3층",
  varLabelSoilw4: "토양 수분(체적), 4층",
  varLabelPrmsl: "해면기압",
  varLabelHeightAtLevel: "{level} hPa 지위고도",
  varLabelTempAtLevel: "{level} hPa 기온",
  varLabelRhAtLevel: "{level} hPa 상대습도",
  varLabelSpfhAtLevel: "{level} hPa 비습",
  varLabelWindAtLevel: "{level} hPa 바람",
  varLabelQfluxAtLevel: "{level} hPa 수증기 플럭스",
  varLabelVvelAtLevel: "{level} hPa 연직 속도",
  varLabelThetaeAtLevel: "{level} hPa 상당온위",
  varLabelCatAtLevel: "{level} hPa 청천난류(EDR)",
  legendSubtropicalHigh: "588 선",
  webglUnavailable: "이 브라우저는 WebGL2 컨텍스트를 제공하지 않습니다",
  pointerRequestFailed: "실시간 포인터 요청이 HTTP {status}를 반환했습니다",
  manifestRequestFailed: "매니페스트 요청이 HTTP {status}를 반환했습니다",
  bundleRequestFailed: "번들 요청이 HTTP {status}를 반환했습니다",
  bundleTooLong: "번들이 매니페스트가 선언한 길이를 넘습니다",
  bundleLengthMismatch: "번들 길이가 매니페스트와 다릅니다",
  bundleChecksumMismatch: "번들 체크섬이 맞지 않습니다",
  bundleRunMismatch: "번들 초기시각이 매니페스트와 다릅니다",
  bundleMissingVariable: "번들에 변수 {id}가 없습니다",
  manifestMissingBundle: "매니페스트에 변수 {id}의 번들이 없습니다",
  mosaicNoMembers: "모자이크를 이루는 위성 데이터를 하나도 사용할 수 없습니다",

  probeAria: "지점 자료 시계열",
  probeHint: "재생하거나 시간축을 끌어 시계열을 채우세요",
  probeComplete: "시계열이 완성되었습니다",
  probeOutside: "이 지점은 데이터셋 격자 밖입니다",
  probeNoData: "이 프레임에는 자료가 없습니다",
  probeAwaiting: "이 프레임의 디코딩을 기다리는 중",
  probeModelElevationLabel: "모델",
  probeDemLabel: "DEM",
  probeCloseAria: "지점 데이터 닫기",
  cardCloseAria: "카드 닫기",

  centerHigh: "고",
  centerLow: "저",

  showcaseLink: "과거 사례",
  showcaseBack: "← 전체 사례",
  showcaseHome: "실시간 예보",
  showcaseTitle: "과거 사례 모음",
  showcaseHeading: "다시 보는 기상 현상",
  showcaseArchive: "아카이브",
  showcaseIntro:
    "각 사례는 지나간 기상 현상의 한 조각입니다. 보관된 예보나 관측을 같은 인코더에 통과시켜, 그 현상이 일어난 영역과 시간만 잘라 두었습니다.",
  showcaseMetaDescription:
    "과거 기상 현상 다시 보기: 태풍·호우·폭염 등을 잘라낸 예보와 레이더 데이터셋",
  showcaseLoading: "사례 목록 읽는 중",
  showcaseEmpty: "공개된 사례가 아직 없습니다",
  showcaseLoadFailed: "사례 목록을 읽지 못했습니다: {message}",
  showcaseCaseMissing: "{id}라는 사례가 없습니다",
  showcaseEventLabel: "현상",
  showcaseRunLabel: "초기시각",
  showcaseSeriesLabel: "시작",
  showcaseRangeLabel: "예보 시간",
  showcaseSpanLabel: "기간",
  showcaseRegionLabel: "영역",
  showcaseGridLabel: "격자",
  showcaseSizeLabel: "용량",
  showcaseVariablesLabel: "요소",
  showcaseHours: "{count} 시간",
  showcaseCaseAria: "사례 {title} 열기",
  showcaseListAria: "과거 사례 목록",

  tcTileTip: "열대저기압",
  varTc: "경로",
  glyphTc: "태",
  tcSheetAria: "열대저기압",
  tcHeading: "열대저기압",
  tcAllSystems: "모든 번호 지정 시스템",
  tcNone: "활동 중인 시스템 없음",
  tcUnnamed: "이름 없는 시스템",
  tcDisturbances: "요란 및 열대저압부",
  tcPotential: "발생 가능 시스템",
  tcPotentialShow: "모델 전용 시스템 {count}개 표시",
  tcModelOnly: "모델 전용",
  tcAgencies: "공식 예보",
  tcModels: "모델 경로",
  tcMembers: "앙상블 멤버",
  tcBest: "최적 경로",
  tcOptionsAria: "태풍 표시 설정",
  tcHistoryHeading: "지난 열대저기압",
  tcHistoryIntro: "아카이브의 번호가 붙은 모든 폭풍: 각 기관의 베스트 트랙, 그 폭풍에 대해 나온 모든 예보, 그리고 각각 얼마나 빗나갔는지.",
  tcHistorySeasonsAria: "시즌",
  tcHistoryBasinsAria: "해역",
  tcHistoryAll: "전체",
  tcHistoryEmpty: "이 시즌과 해역에는 예보가 있는 폭풍이 없습니다",
  tcHistoryStormAria: "{name} 열기",
  tcHistorySouthernNote: "남반구 시즌은 7월부터 이듬해 6월까지이며 끝나는 해로 부릅니다. {season} 시즌은 {from}부터 {to}까지입니다.",
  tcPageTitle: "지난 열대저기압",
  tcPageMetaDescription: "지난 열대저기압: 각 기관의 베스트 트랙, 아카이브된 모든 예보, 그리고 각각 얼마나 빗나갔는지.",
  tcPageLoading: "폭풍을 불러오는 중",
  tcPageMissing: "아카이브에 {id} 폭풍이 없습니다",
  tcPageFailed: "폭풍을 불러오지 못했습니다: {message}",
  tcPageIssue: "시각",
  tcPageNoForecast: "이 시각의 예보 없음",
  tcPagePrev: "이전 시작 시각",
  tcPageNext: "다음 시작 시각",
  tcPageSources: "표시할 경로",
  tcPageErrorsHeading: "진로 오차(km)",
  tcPageTruth: "대조할 베스트 트랙",
  tcPageSource: "출처",
  tcPageErrorsMean: "폭풍 전체 평균",
  tcPageErrorsThis: "지도 위 예보",
  tcPageErrorsNone: "이 폭풍의 예보는 아직 이 베스트 트랙과 대조되지 않았습니다",
  tcPageNote: "종관 시각에 베스트 트랙까지의 거리이며, 시스템이 열대 또는 아열대 저기압인 동안만 셉니다. 앙상블은 평균으로 평가합니다. 공식 예보는 같은 시각에 시작한 모델과 나란히 놓았으며, 이 모델들은 공식 예보가 나온 뒤에 계산을 마쳤습니다.",
  tcPageId: "ATCF 번호",
  tcPageLife: "생애",
  tcPagePeak: "최대 풍속",
  tcPageForecasts: "예보 수",
  tcCardSeasons: "시즌",
  tcCardStorms: "폭풍",
  tcPageDetails: "폭풍 상세 페이지",
  tcPageSeasonList: "이번 시즌 폭풍 목록",
  tcCardBrowse: "시즌별로 보기",
  tcPageRadii: "강풍 반경",
  tcLegendIntensity: "강도: 미국 베스트 트랙, 1분 평균풍",

  soundingTileTip: "라디오존데 관측",
  varSounding: "고층",
  glyphSounding: "탐",
  airportTileTip: "공항 관측",
  varAirport: "METAR",
  glyphAirport: "공",
  synopTileTip: "지상 기상 관측소",
  varSynop: "지상",
  glyphSynop: "지",
  radarTileTip: "단일 사이트 레이더",
  varRadarSite: "사이트",
  glyphRadarSite: "레",
  radarProductAria: "레이더 산출물",
  radarLatest: "최신",
  radarCloseAria: "합성도로 돌아가기",
  radarReflectivity: "반사도",
  radarVelocity: "시선속도",
  stationSounding: "고층관측",
  stationAirport: "공항",
  stationObserved: "관측",
  stationFreezingLevel: "영도 고도",
  stationPw: "가강수량",
  stationCategory: "비행 등급",
  stationVisibility: "시정",
  stationQnh: "QNH",
  stationLevels: "층수",
  stationSynop: "기상 관측소",
  stationHumidity: "습도",
  stationPrecipitation: "강수량",
  stationPressure: "현지 기압",
  stationSeaLevelPressure: "해면 기압",
  stationSunshine: "일조",
  stationSnowDepth: "적설",
  stationElevation: "해발고도",
  stationOpenSounding: "탐공 열기",
  stationPinHere: "여기에 고정",
  fireTileTip: "산불 탐지 (NASA FIRMS, 최근 24시간)",
  varFire: "산불",
  glyphFire: "화",
  fireDetection: "산불 탐지",
  fireAcquired: "관측 시각",
  fireRadiativePower: "화재 복사 강도",
  fireBrightness: "밝기 온도",
  fireConfidence: "신뢰도",
  fireConfidenceLow: "낮음",
  fireConfidenceNominal: "보통",
  fireConfidenceHigh: "높음",
  fireOverpass: "통과",
  fireDay: "주간",
  fireNight: "야간",
  aqTileTip: "대기질 측정소 (PM2.5)",
  varAq: "PM2.5",
  glyphAq: "대",
  aqStation: "대기질",
  aqAgency: "운영 기관",
  aqNetwork: "네트워크",
  aqPreliminary: "잠정 자료: 검증되지 않았으며 변경될 수 있음",
  aqDayRange: "PM2.5, 최근 24시간",
  aqNearest: "가장 가까운 대기질 측정소, {km} km",
  alertsTileTip: "현재 발효 중인 기상 특보",
  varAlerts: "특보",
  glyphAlerts: "경",
  alertsCardTitle: "특보",
  alertsInForceNow: "현재 발효 중",
  alertsAsOf: "{time} 기준",
  alertsLevel: "{level}단계",
  alertsUntilCancelled: "해제 시까지",
  alertsIssued: "{time} 발표",
  alertCategoryRain: "호우",
  alertCategoryFlood: "홍수",
  alertCategoryLandslide: "산사태",
  alertCategoryWind: "강풍",
  alertCategoryThunderstorm: "뇌우",
  alertCategorySnowIce: "눈·결빙",
  alertCategoryHeat: "폭염",
  alertCategoryCold: "한파",
  alertCategoryFog: "안개",
  alertCategoryDust: "황사",
  alertCategoryFire: "화재 위험",
  alertCategoryAir: "대기질",
  alertCategoryCoastal: "해안",
  alertCategoryMarine: "해상",
  alertCategoryTsunami: "지진해일",
  alertCategoryOther: "기타",
  markerPlace: "장소",
  markerEvent: "사건",
  markerSources: "출처",
  caseMarkersAria: "이 사례의 장소와 사건",
  casePartsAria: "이 사건의 구간",
  riverHeading: "하천 유량",
  riverTracksAria: "수문 모델 실행",
  riverTrackAnalysis: "분석",
  riverTrackForecast: "예보 {cycle}",
  riverKeyAria: "평상시 유량 대비 유량",
  riverRatio2: "평상시의 2배 미만",
  riverRatio10: "2–10×",
  riverRatio100: "10–100×",
  riverRatioMax: "≥ 100×",
  riverKeyField: "유량으로 색칠 (색은 레이어 범례와 같음)",
  riverExtents: "침수 범위",
  riverGaugeHint: "원은 유량 관측소입니다. 누르면 수문곡선을 봅니다.",
  riverObserved: "관측",
  riverModelled: "모델",
  riverPeaks: "보고된 첨두",
  riverGaugeLoading: "불러오는 중…",
  riverGaugeUnavailable: "사용할 수 없음",

  soundingObserved: "관측",
  soundingModel: "모델",
  soundingNoModel: "12시간 내 모델 없음",
  soundingLoading: "관측 자료를 읽는 중…",
  soundingUnavailable: "관측 자료 없음",
  soundingExpand: "전체 차트",
  soundingClose: "닫기",
  soundingSheetHeading: "고층 관측",
  soundingSheetAria: "고층 관측 차트",

  // The comparison page (compare.html).
  compareLink: "비교",
  compareTitle: "예보 비교",
  compareHeading: "모델 비교",
  compareEyebrow: "지점",
  compareIntro:
    "한 지점, 한 시간축 위에 운영 중인 예보 모델을 모두 놓습니다. 각 모델이 앞으로의 시간을 어떻게 예보하는지, 가장 가까운 공항의 관측과 나란히 읽을 수 있습니다.",
  compareMetaDescription:
    "GFS·ECMWF·AIFS·IFS HRES·HRRR 예보를 한 지점에서 시간별로 비교하고, 가장 가까운 공항의 METAR 관측과 대조합니다.",
  compareLatitude: "위도",
  compareLongitude: "경도",
  compareLocate: "내 위치 사용",
  compareLocateFailed: "위치를 가져올 수 없습니다",
  compareOnMap: "지도에서 보기",
  compareSpan5: "5일",
  compareSpan15: "15일",
  compareFieldsAria: "비교할 요소",
  compareModelsAria: "모델 비교 블록",
  compareLoading: "{model} 읽는 중…",
  compareModelFailed: "{model}: {message}",
  compareOffGrid: "이 모델의 격자 밖",
  compareNoModels: "이 지점을 덮는 모델이 없습니다",
  compareHide: "숨기기",
  compareShow: "표시",
  comparePin: "맨 위에 고정",
  compareHidden: "숨긴 모델",
  compareRun: "초기시각",
  compareObservationsFrom: "{icao} 관측",
  compareNoAirport: "40 km 이내에 공항이 없습니다",
  compareHours: "시각",
  compareNow: "지금",
  compareCells: "각 값은 그 모델 자체 격자에서 지점에 가장 가까운 격자점의 값이며 보간하지 않습니다. 시각은 사용자의 시간대로 표시됩니다.",
  metricsMetaDescription: "Xue의 각 예보 런이 게시된 시각의 실측값. 게시 워크플로 기록에서 초기 시각 이후의 지연, 누락된 사이클, ERCOT 전일 시장 마감 시점에 게시되어 있던 런을 보여 줍니다.",
  metricsHeading: "런 도착",
  metricsEyebrow: "실측",
  metricsIntro: "각 예보 런이 이 사이트에 게시된 시각을 게시 워크플로 자체 기록에서 읽었습니다. 최신 포인터가 기록된 순간과, 그것이 모델 초기 시각으로부터 얼마나 뒤였는지를 보여 줍니다.",
  metricsNotSla: "이 값은 실측이며 서비스 수준이 아닙니다. Xue는 가용성을 약속하지 않습니다.",
  metricsGenerated: "{time} 생성, 최근 {days}일",
  metricsLoading: "통계를 읽는 중…",
  metricsEmpty: "아직 통계가 없습니다. 통계는 게시 워크플로에서 6시간마다 만들어집니다. 다음 생성 후 다시 확인하세요.",
  metricsFailed: "통계를 읽지 못했습니다: {message}",
  metricsSourcesHeading: "소스별",
  metricsLatencyNote: "지연은 초기 시각부터 런이 게시된 순간까지이므로 모델 자체의 생산 시간을 포함합니다. 모든 시각은 UTC입니다.",
  metricsTimelineNote: "각 띠는 집계 기간 전체입니다. 게시된 사이클마다 초기 시각 위치에 막대 하나, 높이는 지연(그 소스 자체의 최장을 가득 찬 높이로)입니다. 기준선 아래 표시는 도착하지 않은 사이클입니다.",
  metricsTypicalNote: "* 상류 일반값: 모델 자체 데이터가 초기 시각 이후 보통 언제 갖춰지는지에 대한 경험값이며 실측이 아닙니다. 없는 경우 비워 둡니다.",
  metricsTimelineAria: "집계 기간의 {model} 도착",
  metricsCadence: "{hours}시간마다",
  metricsLatest: "최신 런",
  metricsLanded: "게시 시각",
  metricsLatency: "지연",
  metricsMedian: "중앙값",
  metricsMax: "최대",
  metricsTypical: "상류 일반값",
  metricsCycles: "도착한 사이클",
  metricsMissing: "누락",
  metricsMissingList: "누락된 사이클",
  metricsRuns: "워크플로 실행",
  metricsOutcomeLanded: "게시",
  metricsOutcomeSkipped: "건너뜀",
  metricsOutcomeDryRun: "시험 실행",
  metricsOutcomeFailed: "실패",
  metricsBacklog: "아직 읽지 않은 워크플로 실행 {count}건, 다음 생성 때 읽습니다.",
  metricsDeadlinesHeading: "마감 시점",
  metricsDeadlineIntro: "현지 날짜마다 마감 시각에 이미 게시되어 있던 최신 런과, 그때 초기 시각으로부터 지난 시간.",
  metricsSource: "소스",
  metricsCommonest: "보통",
  metricsNoneLive: "없음",
  glyphAurora: "A",
  varAurora: "확률",
  varDis06: "유량",
  varDisrp: "재현기간",
  varUpstream: "유역",
  fieldGroupSpace: "우주기상",
  fieldGroupFire: "산불",
  modelGroupForecast: "수치예보 모델",
  modelGroupRadar: "레이더",
  varLabelAurora: "오로라 관측 확률",
  varLabelDis06: "하천 유량 (6시간 평균)",
  varLabelDisrp: "재현기간 등급",
  varLabelUpstream: "상류 유역 면적",
  creditsAurora: "우주기상: 오로라 확률, NOAA SWPC OVATION 모델",
  creditsEfas: "하천 유량: Copernicus EMS EFAS v5, 유럽 1′; 사례 전용",
  creditsEra5: "재분석: Copernicus C3S ERA5(ECMWF), 전 지구 0.25°, 매시, CC-BY-4.0; 사례 전용",
  creditsCams: "에어로졸 분석: Copernicus CAMS(ECMWF), 전 지구 0.4°, 3시간 간격, CC-BY-4.0; 사례 전용",
};
